// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import { generateId, handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import RouteBuilder from '@Services/RouteBuilder';
import client from '@Client';
import { computeEventList } from '@Organisms/ReportTemplate/eventList';
import useOneQuery from '@Hooks/useOneQuery';
import { ReportTemplateOutput, ReportTemplateWidget } from 'thehive-sdk';
import { computeGetSelector } from '@Organisms/ReportTemplate/selector';
import { THLayout } from '@Molecules/THLayout';
import { ReportTemplateManage } from '@Organisms/ReportTemplate/ReportTemplateManage/ReportTemplateManage';
import { OrgSwitcherContext } from '@Context/OrgSwitcherProvider';

import './ReportTemplatesDetails.scss';

export const ReportTemplatesDetails = (): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();
    const { reportTemplateId, orgId } = useParams<{ reportTemplateId: string; orgId: string }>();
    const [previewMode, setPreviewMode] = useState(false);
    const [widgets, setWidgets] = useState<ReportTemplateWidget[]>();
    const [header, setHeader] = useState('');
    const [footer, setFooter] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const { setBlockOrgSwitch } = useContext(OrgSwitcherContext);

    const {
        data: reportTemplateData,
        refetch,
        error,
    } = useOneQuery<ReportTemplateOutput>({
        selector: computeGetSelector({ reportTemplateId }),
        controller: client.ReportTemplateController,
        fetchOnStart: !!reportTemplateId,
        manual: true,
    });

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.ReportTemplateController,
                locale: i,
                onSuccess: (_, message) => {
                    message && openMessage('success', message);
                    refetch();
                },
                onError: handleNotificationError,
            }),
        [refetch, i],
    );

    useEffect(() => {
        setWidgets(reportTemplateData?.definition?.widgets?.map((widget) => ({ ...widget, _id: generateId() })));
        setHeader(reportTemplateData?.definition?.header?.template || '');
        setFooter(reportTemplateData?.definition?.footer?.template || '');
    }, [reportTemplateData?.definition]);

    useEffect(() => {
        if (error) {
            openMessage('error', i('report-template.create.error'));
            history.push(RouteBuilder.compute('organisation.templates-report', { orgId }));
        }
    }, [error, history, i, orgId]);

    useEffect(() => {
        window.onbeforeunload = isDirty ? (): boolean | null => true : null;
        if (isDirty) {
            setBlockOrgSwitch(true);
        }

        return () => {
            window.onbeforeunload = null;
            setBlockOrgSwitch(false);
        };
    }, [isDirty, setBlockOrgSwitch]);

    return (
        <THLayout>
            <THLayout.Content>
                <Prompt when={isDirty} message={() => i('app.general.beforeunload')} />
                <div className="report-template-details__content">
                    {reportTemplateData && !!widgets && (
                        <ReportTemplateManage
                            header={header}
                            footer={footer}
                            widgets={widgets}
                            previewMode={previewMode}
                            setPreviewMode={setPreviewMode}
                            setWidgets={setWidgets}
                            setHeader={setHeader}
                            setFooter={setFooter}
                            onEdit={async () => {
                                if (reportTemplateData) {
                                    await eventList.onEdit(
                                        {
                                            definition: {
                                                ...reportTemplateData?.definition,
                                                widgets: widgets.filter(({ _kind }) => !!_kind),
                                                header: {
                                                    template: header,
                                                },
                                                footer: {
                                                    template: footer,
                                                },
                                            },
                                            title: reportTemplateData?.title,
                                        },
                                        reportTemplateId,
                                    );
                                }
                                await setIsDirty(false);
                            }}
                            onCancel={async () => {
                                setWidgets(reportTemplateData?.definition.widgets || []);
                                setHeader(reportTemplateData?.definition.header?.template || '');
                                setFooter(reportTemplateData?.definition.footer?.template || '');
                                await setIsDirty(false);
                                history.push(RouteBuilder.compute('organisation.templates-report', { orgId }));
                            }}
                            reportTemplateId={reportTemplateData._id}
                            setIsDirty={setIsDirty}
                        />
                    )}
                </div>
            </THLayout.Content>
        </THLayout>
    );
};
